/**
 * The rules of a pitch email (spec 2026-10-11 §4.4, §8.4, §11): plain text, 60-140 words, one link
 * (the pitch page), no money. Pure.
 */

export const PITCH_MIN_WORDS = 60;
export const PITCH_MAX_WORDS = 140;
export const PITCH_MAX_LINKS = 1;

export type PitchLintHint = 'tooShort' | 'tooLong' | 'tooManyLinks' | 'mentionsMoney' | 'missingPitchLink';

const URL_PATTERN = /\bhttps?:\/\/[^\s<>()]+|\bwww\.[^\s<>()]+/gi;

/**
 * Words that offer or ask money for a placement. A pitch never holds them (Spotify rules, payola).
 * "$50", "50 USD", "pay", "paid", "fee", "promotion package", "compensation", "budget".
 */
const MONEY_PATTERNS: RegExp[] = [
  /[$€£]\s?\d/,
  /\d\s?(?:usd|eur|gbp|dollars?|euros?|bucks)\b/i,
  /\bpa(?:y|ys|ying|id|yment|yments)\b/i,
  /\bfees?\b/i,
  /\bpromotion(?:al)? packages?\b/i,
  /\bcompensat\w*/i,
  /\bbudget\b/i,
  /\bsponsor(?:ed|ship)?\b/i,
];

export function countWords(text: string): number {
  return text
    .replace(URL_PATTERN, ' ')
    .split(/\s+/)
    .filter((word) => /[\p{L}\p{N}]/u.test(word)).length;
}

/** The hints of one pitch body. An empty list means the body passes. */
export function pitchLint(body: string, opts: { pitchUrl: string }): PitchLintHint[] {
  const hints: PitchLintHint[] = [];
  const words = countWords(body);
  if (words < PITCH_MIN_WORDS) {
    hints.push('tooShort');
  }
  if (words > PITCH_MAX_WORDS) {
    hints.push('tooLong');
  }
  const links = body.match(URL_PATTERN) ?? [];
  if (links.length > PITCH_MAX_LINKS) {
    hints.push('tooManyLinks');
  }
  // The pitch link is ours: its random token can hold a money pattern ("…5eur") by chance.
  const ownText = opts.pitchUrl === '' ? body : body.split(opts.pitchUrl).join(' ');
  if (MONEY_PATTERNS.some((pattern) => pattern.test(ownText))) {
    hints.push('mentionsMoney');
  }
  if (opts.pitchUrl === '' || !body.includes(opts.pitchUrl)) {
    hints.push('missingPitchLink');
  }
  return hints;
}

/** The most characters of a pitch subject (design r1: who it is for, the song, the artist). */
export const PITCH_SUBJECT_MAX_LENGTH = 90;

export type PitchSubjectHint = 'subjectEmpty' | 'subjectTooLong' | 'subjectShouts';

/** The hints of one pitch subject. An empty list means the subject passes. */
export function pitchSubjectLint(subject: string): PitchSubjectHint[] {
  const text = subject.trim();
  if (text === '') {
    return ['subjectEmpty'];
  }
  const hints: PitchSubjectHint[] = [];
  if (text.length > PITCH_SUBJECT_MAX_LENGTH) {
    hints.push('subjectTooLong');
  }
  const letters = text.replace(/[^\p{L}]/gu, '');
  if (letters.length >= 8 && letters === letters.toUpperCase() && letters !== letters.toLowerCase()) {
    hints.push('subjectShouts');
  }
  return hints;
}

/** One line with single spaces: the clean-up of every part of a subject (as the AI subject). */
function oneLine(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * The subject of design r1, built by code: `For <target>: "<Song>" by <Artist>`. Each part with
 * no value is left out. The subject names no place: we have no origin of the artist. The writer
 * uses it when the AI subject fails `pitchSubjectLint`.
 */
export function buildPitchSubject(input: { targetName: string | null; songTitle: string; artistName: string }): string {
  const target = oneLine(input.targetName);
  const artist = oneLine(input.artistName);
  const song = `"${oneLine(input.songTitle)}"${artist === '' ? '' : ` by ${artist}`}`;
  const full = `${target === '' ? '' : `For ${target}: `}${song}`;
  // Too long: the target goes first.
  return (full.length <= PITCH_SUBJECT_MAX_LENGTH ? full : song).slice(0, PITCH_SUBJECT_MAX_LENGTH);
}

/**
 * The footer under every pitch and follow-up:
 *
 *   Sent with Music24 for <artist> · Not interested? Reply "stop" or open <stop url>
 *   <source line, step 1 only>
 *   <postal address of the sender, when set>
 */
export function outreachFooter(input: {
  artistName: string;
  stopUrl: string;
  sourceLine: string | null;
  senderPostal: string | null;
}): string {
  const lines = [`Sent with MUSIC24 for ${input.artistName} · Not interested? Reply "stop" or open ${input.stopUrl}`];
  const source = input.sourceLine?.trim() ?? '';
  if (source !== '') {
    lines.push(source);
  }
  const postal = input.senderPostal?.replace(/\s*\n\s*/g, ', ').trim() ?? '';
  if (postal !== '') {
    lines.push(postal);
  }
  return lines.join('\n');
}
